import { observer } from 'mobx-react';
import React, { ChangeEvent, useCallback, useMemo } from 'react';
import itemsStore from '../../store/itemsStore';
import { IItemModifierOptionValidation, IItemModifierValidation } from '../../types/items';
import integrationsStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import { TBooleanInNumber } from 'src/types/main';
import { IProductV4Modifier, IProductV4ModifierOption } from 'src/types/product';
import { formatUnit } from 'src/utils/formatter';
import { booleanNumberReverse, getTranslate, isDisabledByPos } from 'src/utils/helpers';
import { Checkbox } from 'src/view/components/Controls/Checkbox/Checkbox';
import CustomTooltip, { TOOLTIP_DURATION, TOOLTIP_POSITION } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import InputAlt from 'src/view/components/Controls/InputAlt/InputAlt';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import Text, { TEXT } from 'src/view/components/Text/Text';
import MenuManagTabItemPriceInput from 'src/view/screens/MenuManagementScreen/components/MenuManagTabItemPriceInput/MenuManagTabItemPriceInput';
import './style.scss';

interface IProps {
  modifier: IProductV4Modifier;
  modifierValid?: IItemModifierValidation;
  option: IProductV4ModifierOption;
}

const ItemModifierOption: React.FC<IProps> = observer((props: IProps): JSX.Element => {
  const optionValid: IItemModifierOptionValidation | undefined = props.modifierValid?.options.find((option) => option.id === props.option.id);

  const changePreselect = useCallback(() => {
    itemsStore.updateModifierOption(props.modifier.id, props.option.id, { is_default: booleanNumberReverse(props.option.is_default) });
  }, [props.modifier, props.option]);

  const changeAvailability = useCallback(
    (status: TBooleanInNumber) => {
      itemsStore.updateModifierOption(props.modifier.id, props.option.id, { status });
      // update validation
      if (props.modifierValid && !props.modifierValid.quantitySumm) itemsStore.setValidation();
    },
    [props.modifier.id, props.modifierValid, props.option.id]
  );

  const changeQuantity = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { modifier, option } = props;
      const value: string = event.target.value.replace(/\D/, '');
      const valueTNumber: number = Number(value);

      itemsStore.updateModifierOption(modifier.id, option.id, { maximum: !value || isNaN(valueTNumber) ? undefined : valueTNumber });
      // update validation
      if ((optionValid && !optionValid.quantity) || (props.modifierValid && !props.modifierValid.quantitySumm)) itemsStore.setValidation();
    },
    [props.modifier, props.option, optionValid, props.modifierValid]
  );

  const changePrice = useCallback(
    (price: string) => {
      itemsStore.updateModifierOption(props.modifier.id, props.option.id, { price });
    },
    [props.modifier, props.option]
  );

  // if modifier has a minimum, then it is forbidden to disable all options
  const minAvailableOptions = useMemo((): boolean => {
    if (isDisabledByPos(integrationsStore.posRestrictions?.itemOptionGlobalAvailability) || !props.modifier?.minimum || !props.option.status) {
      return true;
    }

    return props.modifier.options.filter((option) => !option.is_delete && option.id !== props.option.id).some((option) => option.status);
  }, [props.option.id, props.modifier?.minimum, props.modifier.options, props.option.status]);

  return (
    <li className="item-modifier-option">
      <div className="item-modifier-option__no-validation-block-wrapper w-10">
        <IntegrationManager
          setting={integrationsStore.posRestrictions?.preSelectedOptions}
          wrapperClassName="w-43"
          withTooltip
          tooltipOptions={{
            wrapperClassName: 'w-fit-content',
          }}
        >
          <Checkbox
            value={Boolean(props.option.is_default)}
            name={'option-' + getTranslate(props.option.name) + '-preselect'}
            onClick={changePreselect}
          />
        </IntegrationManager>
      </div>
      <div className="item-modifier-option__no-validation-block-wrapper pr-4 w-18">
        <Text type={TEXT.LIST_ROW} text={props.option.name} className="pr-2" />
      </div>
      <MenuManagTabItemPriceInput
        price={props.option.price}
        validation={{
          state: optionValid ? optionValid.price : true,
          errorText: localeStore.t('required_field_error'),
        }}
        posRestriction={integrationsStore.posRestrictions?.itemOptionGlobalPrice}
        changePrice={changePrice}
        setValidation={() => itemsStore.setValidation()}
        className="item-modifier-option__input-price"
        wrapperClassName="w-15"
      />
      <InputAlt
        // space required !!!
        value={props.option.maximum?.toString() || ' '}
        onInput={changeQuantity}
        disabled={Boolean(props.modifier.unique_options)}
        validation={{
          state: optionValid ? optionValid.quantity : true,
          errorText: localeStore.t('required_field_error'),
          styleType: 'bottom',
        }}
        wrapperClassName="w-18"
        className="item-modifier-option__input-quantity py-0"
      />
      <div className="item-modifier-option__no-validation-block-wrapper w-39">
        <Text type={TEXT.LIST_ROW} text={formatUnit(props.option.unit, Number(props.option.weight))} className="w-38" noTranslate />
        <Text type={TEXT.LIST_ROW} text={props.option.calories} className="w-39" noTranslate />
        <IntegrationManager setting={integrationsStore.posRestrictions?.itemOptionGlobalAvailability} withTooltip>
          <CustomTooltip
            disabled={minAvailableOptions}
            message="min_options_is_not_enough"
            position={TOOLTIP_POSITION.FIXED}
            duration={TOOLTIP_DURATION.EXTRA_LARGE}
            maxWidth={300}
          >
            <Switcher disabled={!minAvailableOptions} status={props.option.status || 0} onChange={changeAvailability} />
          </CustomTooltip>
        </IntegrationManager>
      </div>
    </li>
  );
});

export default ItemModifierOption;
